import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SearchBar from '../components/SearchBar';
import EventCard from '../components/EventCard';
import { getEvents, getFilteredEvents } from '../services/eventService';
import { Event } from '../types';
import { useTranslation } from 'react-i18next';
import { logAnalyticsEvent } from '../firebase/config';

const EventsPage: React.FC = () => {
  const location = useLocation();
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams(location.search);
        const categoryParam = params.get('category');
        const cityParam = params.get('city');
        const dateParam = params.get('date');

        if (categoryParam || cityParam || dateParam) {
          setFilteredEvents(await getFilteredEvents(cityParam || undefined, categoryParam || undefined, dateParam || undefined));
        } else {
          setFilteredEvents(await getEvents());
        }
      } catch {
        setError(t('error'));
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [location.search, t]);

  const handleSearch = async (filters: { city: string; category: string; date: string }) => {
    try {
      setLoading(true);
      setFilteredEvents(await getFilteredEvents(filters.city || undefined, filters.category || undefined, filters.date || undefined));
      logAnalyticsEvent('search', { search_term: JSON.stringify(filters) });
    } catch {
      setError(t('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-background min-h-screen">
      <div className="max-w-container mx-auto px-4 md:px-margin-desktop py-10">

        <h1 className="font-headline text-headline-md text-on-surface mb-6">{t('events.title')}</h1>

        <div className="mb-8">
          <SearchBar onSearch={handleSearch} />
        </div>

        {error && (
          <div className="bg-error-container text-on-error-container px-4 py-3 rounded-xl font-body text-body-md mb-6">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-r-transparent" />
          </div>
        ) : (
          <>
            <p className="font-headline text-label-md text-on-surface-variant mb-6">
              {t('events.eventsFound', { count: filteredEvents.length })}
            </p>

            {filteredEvents.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredEvents.map(event => (
                  <EventCard key={event.id} event={event} variant="card" />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 bg-surface-container-lowest rounded-2xl border border-outline-variant">
                <span className="material-symbols-outlined text-[64px] text-on-surface-variant mb-4">event_busy</span>
                <p className="font-headline text-headline-sm text-on-surface-variant mb-6">{t('events.noEvents')}</p>
                <button
                  onClick={() => handleSearch({ city: '', category: '', date: '' })}
                  className="bg-primary text-on-primary px-6 py-3 rounded-lg font-headline text-label-md hover:brightness-110 transition-all active:scale-95"
                >
                  {t('events.resetFilters')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EventsPage;
